import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  LuChevronLeft,
  LuExternalLink,
  LuLoaderCircle,
  LuX,
} from "react-icons/lu";
import { ChatApi } from "../../../api/chatApi";
import { RANKINGS_API_BASE_URL, RankingsApi } from "../../../api/rankingsApi";
import { useIsUserSignedIn } from "../../../core_stores/useAuthStore";
import { useIsCompactView } from "../../../core_stores/useIsCompactViewStore";
import { useSubscriptionStore, useSubscriptionActions } from "../../../core_stores/useSubscriptionStore";
import { colors, typography } from "../../../design/tokens";
import { AppDetailResponse, StoreType } from "../../../model/rankings";
import { useAuthModal } from "../../../providers/AuthModalProvid";
import { useSnackbar } from "../../../providers/SnackbarProvider";
import { useSubscriptionsModal } from "../../../providers/SubscriptionsModalProvider";
import { AppIcon } from "../../common_components/AppIcon";

const MIN_TOKENS_FOR_NEW_PROJECT = 20000;

interface AppDetailModalProps {
  store: StoreType;
  appId: string;
  country: string;
  onClose: () => void;
}

export function AppDetailModal({
  store,
  appId,
  country,
  onClose,
}: AppDetailModalProps) {
  const navigate = useNavigate();
  const isCompactView = useIsCompactView();
  const isUserSignedIn = useIsUserSignedIn();
  const { show: showAuthModal } = useAuthModal();
  const { open: openSubscriptionsModal } = useSubscriptionsModal();
  const { showSnackbar } = useSnackbar();
  const { reloadSubscriptionsInfo } = useSubscriptionActions();
  const [appDetail, setAppDetail] = useState<AppDetailResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [showScreenshotConfirm, setShowScreenshotConfirm] = useState(false);
  const [pendingPrompt, setPendingPrompt] = useState<string>("");
  const [isCreatingProject, setIsCreatingProject] = useState(false);
  const [isFetchingPreview, setIsFetchingPreview] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);

  // Fetch app details
  useEffect(() => {
    const fetchDetails = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const data = await RankingsApi.fetchAppDetail(store, appId, country);
        setAppDetail(data);
      } catch (err) {
        setError("Failed to load app details");
        console.error("Failed to fetch app details:", err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchDetails();
  }, [store, appId, country]);

  // Prevent background scroll
  // Only restore scroll if this is the last modal (search modal must handle its own cleanup)
  useEffect(() => {
    const scrollbarWidth =
      window.innerWidth - document.documentElement.clientWidth;

    // Store the previous overflow state in case another modal had set it
    const previousOverflow = document.body.style.overflow;
    const previousPadding = document.body.style.paddingRight;

    document.body.style.overflow = "hidden";
    document.body.style.paddingRight = `${scrollbarWidth}px`;

    return () => {
      // Restore only if search modal isn't still open
      const searchModal = document.querySelector('[data-modal="search-results"]');
      if (!searchModal) {
        document.body.style.overflow = previousOverflow || "";
        document.body.style.paddingRight = previousPadding || "";
      }
    };
  }, []);

  // Handle ESC key and back button
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  // Handle browser back button by pushing a state to history
  useEffect(() => {
    // Push a history state when modal opens so back button will trigger popstate
    window.history.pushState({ modalOpen: true }, "");

    const handlePopState = (e: PopStateEvent) => {
      if (e.state?.modalOpen) {
        // This state was created by the modal, just close it
        onClose();
      } else {
        // This is from before the modal was opened, let normal navigation happen
        onClose();
      }
    };

    window.addEventListener("popstate", handlePopState);
    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [onClose]);

  const formatNumber = (num: number): string => {
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(1)}M`;
    } else if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}K`;
    }
    return num.toString();
  };

  const formatDate = (dateStr: string | number): string => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const generatePrompt = (app: AppDetailResponse): string => {
    const parts: string[] = [];

    // Start with legally safe inspiration-based instruction
    parts.push(`Take inspiration from the concept of "${app.name}" and create a unique app with similar functionality.`);

    // Category/Genre
    const category = app.primaryGenre || app.genre;
    if (category) {
      parts.push(`\n\nCategory: ${category}`);
    }

    // Description - include if available as reference only
    if (app.description) {
      parts.push(`\n\nReference description for inspiration:\n${app.description}`);
    }

    // Add clear instruction emphasizing originality
    parts.push(`\n\nPlease create an original app inspired by this concept, with your own unique implementation, modern design, and clean UI. Do not copy any proprietary elements, branding, or trademarked content.`);

    return parts.join('');
  };

  const handleCreateAppLikeThis = async () => {
    if (!appDetail) return;

    // Require login
    if (!isUserSignedIn) {
      await showAuthModal();
      return;
    }

    // Always refresh token balance before checking
    await reloadSubscriptionsInfo();

    // Check if user has enough tokens
    const subscription = useSubscriptionStore.getState().subscription;
    // Round to integer to treat fractional tokens correctly
    if (!subscription || Math.round(subscription.remaining) < MIN_TOKENS_FOR_NEW_PROJECT) {
      showSnackbar(
        "Not enough tokens to start a new project. At least 20,000 tokens required.",
        { type: "error" }
      );
      openSubscriptionsModal();
      return;
    }

    const prompt = generatePrompt(appDetail);
    setPendingPrompt(prompt);
    setShowScreenshotConfirm(true);
  };

  const handleConfirmWithScreenshots = async (includeScreenshots: boolean) => {
    setIsFetchingPreview(true);

    // Allow React to render the spinner before continuing with async operations
    await new Promise(resolve => setTimeout(resolve, 0));

    try {
      // Create new project first
      const newProjectId = await ChatApi.createNewProject();

      // Fetch preview image if requested
      let imageFile: File | null = null;
      if (includeScreenshots && appDetail) {
        try {
          const encodedId = encodeURIComponent(appDetail.id);
          const previewUrl = `${RANKINGS_API_BASE_URL}/app-rankings/app/preview?id=${encodedId}&store=${store}&country=${country}`;
          const response = await fetch(previewUrl);
          if (response.ok) {
            const blob = await response.blob();
            if (blob.size > 0) {
              const safeName = appDetail.name.replace(/[^a-zA-Z0-9]/g, '-');
              imageFile = new File([blob], `${safeName}-preview.jpg`, { type: 'image/jpeg' });
            }
          }
        } catch (err) {
          console.error('Failed to fetch preview image:', err);
          // Continue without image if fetch fails
        }
      }

      // Reset modal states before navigation
      setIsFetchingPreview(false);
      setShowScreenshotConfirm(false);
      onClose(); // Close parent App Detail modal

      // Navigate to the new project with initialPrompt state
      // ProjectPage will handle sending the message and showing temporary message
      navigate(`/project/${newProjectId}`, {
        state: {
          projectId: newProjectId,
          initialPrompt: pendingPrompt,
          initialImage: imageFile,
        },
      });
    } catch (error) {
      console.error('Failed to create project:', error);
      setIsFetchingPreview(false);
      setShowScreenshotConfirm(false);
      setIsCreatingProject(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        ref={modalRef}
        className={`relative bg-dark-surface-primary rounded-2xl shadow-[0_4px_24px_rgba(0,0,0,0.5)] flex flex-col ${
          isCompactView
            ? "w-full h-full rounded-none"
            : "max-w-[700px] w-full mx-4 max-h-[90vh]"
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className={`absolute top-4 z-20 ${colors.text.secondary} hover:text-white transition-colors ${
            isCompactView ? "left-4" : "right-4"
          }`}
        >
          {isCompactView ? <LuChevronLeft size={24} /> : <LuX size={24} />}
        </button>

        {/* Content */}
        <div className="flex-1 min-h-0 overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-20">
              <LuLoaderCircle
                className={`animate-spin ${colors.text.secondary}`}
                size={40}
              />
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-20 px-4">
              <p className={`${colors.text.secondary} text-center`}>{error}</p>
            </div>
          ) : appDetail ? (
            <div className="p-6 pt-12">
              {/* Header - Icon, Name, Developer */}
              <div className="flex items-start gap-4 mb-6">
                <AppIcon
                  src={appDetail.icon}
                  alt={appDetail.name}
                  size="large"
                  lazy={false}
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h2
                      className={`${typography.size.lg} ${typography.weight.semibold} ${colors.text.primary}`}
                    >
                      {appDetail.name}
                    </h2>
                    {appDetail.url && (
                      <a
                        href={appDetail.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`${colors.text.secondary} hover:text-white transition-colors flex-shrink-0`}
                        title={store === "ios" ? "View in App Store" : "View in Google Play"}
                      >
                        <LuExternalLink size={18} />
                      </a>
                    )}
                  </div>
                  <p className={`${typography.size.sm} ${colors.text.secondary}`}>
                    {appDetail.developer}
                  </p>
                </div>
              </div>

              {/* Screenshots carousel */}
              {appDetail.screenshots && appDetail.screenshots.length > 0 && (
                <div className="relative mb-6">
                  <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
                    {appDetail.screenshots
                      .slice(0, isCompactView ? 3 : 5)
                      .map((screenshot, index) => (
                        <img
                          key={index}
                          src={screenshot}
                          alt={`Screenshot ${index + 1}`}
                          className="h-[200px] w-auto rounded-lg object-cover flex-shrink-0"
                          loading="lazy"
                          referrerPolicy="no-referrer"
                        />
                      ))}
                  </div>
                  {!isCompactView && appDetail.screenshots.length > 5 && (
                    <div className="absolute right-0 top-0 bottom-2 w-16 bg-gradient-to-l from-dark-surface-primary to-transparent pointer-events-none" />
                  )}
                </div>
              )}

              {/* Stats row */}
              <div className="flex items-center justify-around py-4 mb-6 border-y border-dark-border">
                {/* Rating */}
                <div className="text-center">
                  <div className="flex items-center justify-center gap-1 mb-1">
                    <span
                      className={`${typography.size.lg} ${typography.weight.semibold} ${colors.text.primary}`}
                    >
                      {appDetail.rating?.toFixed(1) || "N/A"}
                    </span>
                  </div>
                  <span className={`${typography.size.xs} ${colors.text.secondary}`}>
                    Rating
                  </span>
                </div>

                {/* Reviews */}
                <div className="text-center">
                  <span
                    className={`${typography.size.lg} ${typography.weight.semibold} ${colors.text.primary} block mb-1`}
                  >
                    {appDetail.reviews ? formatNumber(appDetail.reviews) : "N/A"}
                  </span>
                  <span className={`${typography.size.xs} ${colors.text.secondary}`}>
                    Reviews
                  </span>
                </div>

                {/* Installs */}
                {appDetail.installs && (
                  <div className="text-center">
                    <span
                      className={`${typography.size.lg} ${typography.weight.semibold} ${colors.text.primary} block mb-1`}
                    >
                      {appDetail.installs}
                    </span>
                    <span className={`${typography.size.xs} ${colors.text.secondary}`}>
                      Installs
                    </span>
                  </div>
                )}

                {/* Price */}
                <div className="text-center">
                  <span
                    className={`${typography.size.lg} ${typography.weight.semibold} ${colors.text.primary} block mb-1`}
                  >
                    {appDetail.price}
                  </span>
                  <span className={`${typography.size.xs} ${colors.text.secondary}`}>
                    Price
                  </span>
                </div>
              </div>

              {/* Description */}
              <div className="mb-6">
                <h3
                  className={`${typography.size.sm} ${typography.weight.semibold} ${colors.text.primary} mb-2`}
                >
                  Description
                </h3>
                <div
                  className={`${typography.size.sm} ${colors.text.secondary} leading-relaxed whitespace-pre-line ${
                    !showFullDescription ? "line-clamp-4" : ""
                  }`}
                >
                  {appDetail.description}
                </div>
                {appDetail.description && appDetail.description.length > 200 && (
                  <button
                    onClick={() => setShowFullDescription(!showFullDescription)}
                    className="text-blue-400 text-sm mt-2 hover:underline"
                  >
                    {showFullDescription ? "Show less" : "Show more"}
                  </button>
                )}
              </div>

              {/* Metadata table */}
              <div className="space-y-3">
                <MetadataRow label="Version" value={appDetail.version} />
                <MetadataRow label="Size" value={appDetail.size} />
                <MetadataRow
                  label="Updated"
                  value={appDetail.updated ? formatDate(appDetail.updated) : undefined}
                />
                <MetadataRow label="Content Rating" value={appDetail.contentRating} />
                <MetadataRow
                  label="Category"
                  value={appDetail.primaryGenre || appDetail.genre}
                />
                {appDetail.IAPRange && (
                  <MetadataRow label="In-App Purchases" value={appDetail.IAPRange} />
                )}
                {appDetail.adSupported && (
                  <MetadataRow label="Contains Ads" value="Yes" />
                )}
                {appDetail.languages && appDetail.languages.length > 0 && (
                  <MetadataRow
                    label="Languages"
                    value={appDetail.languages.slice(0, 5).join(", ") +
                      (appDetail.languages.length > 5
                        ? ` +${appDetail.languages.length - 5} more`
                        : "")}
                  />
                )}
              </div>
            </div>
          ) : null}
        </div>

        {/* Fixed footer button */}
        {appDetail && (
          <div className={`flex-shrink-0 border-t ${colors.border.primary} p-4`}>
            <button
              onClick={handleCreateAppLikeThis}
              disabled={isCreatingProject}
              className="w-full bg-primary-blue text-white text-sm font-semibold tracking-wide py-3 rounded-full hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isCreatingProject ? "Creating project..." : "Build Something Like This"}
            </button>
          </div>
        )}
      </div>

      {/* Confirmation dialog */}
      {showScreenshotConfirm && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm"
          onClick={(e) => {
            if (e.target === e.currentTarget && !isFetchingPreview) {
              setShowScreenshotConfirm(false);
            }
          }}
        >
          <div
            className={`relative ${colors.surface.primary} rounded-2xl p-6 max-w-md mx-4`}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            {!isFetchingPreview && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowScreenshotConfirm(false);
                }}
                className={`absolute top-4 right-4 ${colors.text.secondary} hover:text-white transition-colors`}
              >
                <LuX size={24} />
              </button>
            )}

            {isFetchingPreview ? (
              <div className="flex flex-col items-center justify-center py-8">
                <LuLoaderCircle
                  className={`animate-spin ${colors.text.secondary} mb-4`}
                  size={40}
                />
                <p className={`${typography.size.sm} ${colors.text.secondary} text-center`}>
                  Creating your project...
                </p>
              </div>
            ) : (
              <>
                <h3 className={`${typography.size.lg} ${typography.weight.semibold} ${colors.text.primary} mb-3`}>
                  Include App Screenshots?
                </h3>
                <p className={`${typography.size.sm} ${colors.text.secondary} mb-5`}>
                  Would you like to include screenshots as visual reference for your new project?
                </p>

                {/* What to Expect section */}
                <div className={`${colors.surface.secondary} border ${colors.border.primary} rounded-lg p-3 mb-4`}>
                  <p className={`${typography.size.sm} ${typography.weight.semibold} ${colors.text.primary} mb-2`}>
                    What to Expect
                  </p>
                  <p className={`${typography.size.sm} ${colors.text.secondary}`}>
                    Darvin builds the frontend/UI from the app description and screenshots. Great for simple games and offline apps. Backend features not included. Token usage can be higher than for typical projects.
                  </p>
                </div>

                {/* Legal disclaimer */}
                <p className={`${typography.size.xs} ${colors.text.tertiary} mb-6`}>
                  Your app will be original. All branding, trademarks, and proprietary elements will be replaced with unique alternatives.
                </p>

                <div className="flex gap-3">
                  <button
                    onClick={() => handleConfirmWithScreenshots(false)}
                    className={`flex-1 border ${colors.border.primary} ${colors.text.primary} ${typography.size.sm} ${typography.weight.semibold} tracking-wide py-2.5 rounded-full hover:bg-white/5 transition-colors`}
                  >
                    No, skip
                  </button>
                  <button
                    onClick={() => handleConfirmWithScreenshots(true)}
                    className={`flex-1 bg-primary-blue ${colors.text.primary} ${typography.size.sm} ${typography.weight.semibold} tracking-wide py-2.5 rounded-full ${colors.hover.opacity} transition-opacity`}
                  >
                    Yes, include
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function MetadataRow({
  label,
  value,
}: {
  label: string;
  value?: string | null;
}) {
  if (!value) return null;

  return (
    <div className="flex justify-between items-start">
      <span className={`${typography.size.sm} ${colors.text.secondary}`}>
        {label}
      </span>
      <span
        className={`${typography.size.sm} ${colors.text.primary} text-right max-w-[60%]`}
      >
        {value}
      </span>
    </div>
  );
}
